import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Play, Download, Loader2, AlertTriangle } from "lucide-react";

const FILES = [["stores", "01 Stores"], ["customers", "02 Customers"], ["points", "03 Points / tiers"],
  ["bills", "04 Bills"], ["customer_aggregates", "Customer aggregates"], ["sku_lines", "05 SKU lines"]];
const ACTIVE = ["staging", "queued", "running", "paused_disk"];
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
const gb = (b) => (b == null ? "—" : `${(b / 1024 ** 3).toFixed(2)} GB`);

function JobTable({ job }) {
  return (
    <table className="w-full text-xs" data-testid="ingest-files-table">
      <thead className="text-[10px] uppercase tracking-widest text-neutral-500 text-left">
        <tr><th className="py-2">File</th><th>Source rows</th><th>Inserted</th><th>Updated</th><th>Already present</th><th>Skipped</th><th>Errors</th><th>Skip reasons</th></tr>
      </thead>
      <tbody>
        {FILES.map(([k, label]) => {
          const s = job.files?.[k];
          return (
            <tr key={k} className="border-t border-black/5 align-top" data-testid={`ingest-row-${k}`}>
              <td className="py-2 font-medium">{label}{job.stage === k && job.status === "running" && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}</td>
              <td className="font-mono">{n(s?.source)}</td><td className="font-mono">{n(s?.inserted)}</td>
              <td className="font-mono">{n(s?.updated)}</td><td className="font-mono">{n(s?.existing)}</td>
              <td className="font-mono">{n(s?.skipped)}</td><td className="font-mono">{n(s?.errors)}</td>
              <td className="max-w-[280px]">{Object.entries(s?.reasons || {}).map(([r, c]) => <div key={r}>{r}: {n(c)}</div>)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function JobCard({ job, reload, canStart }) {
  const active = ACTIVE.includes(job.status);
  const dl = async () => {
    const r = await api.get(`/data-files/ingests/${job.id}/skipped.csv`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `skipped_${job.id.slice(0, 8)}.csv`;
    a.click();
  };
  const cancel = async () => { await api.post(`/data-files/ingests/${job.id}/cancel`); toast("Cancel requested"); reload(); };
  const resume = async () => {
    try { const r = await api.post(`/data-files/ingests/${job.id}/resume`); toast.success(`Resuming from ${r.data.resumed_from}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resume"); }
  };
  const pts = job.files?.points?.bal_points_sum_loaded;
  return (
    <div className="border border-black/10 p-4 space-y-3" data-testid={`ingest-job-${job.id}`}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <div>
          <span className={`font-semibold ${job.status === "paused_disk" || job.status === "failed" ? "text-red-700" : ""}`} data-testid="ingest-status">{job.status === "paused_disk" ? "Paused — database disk nearly full" : job.status}</span>
          <span className="text-neutral-500"> · {job.message}</span>
        </div>
        <div className="text-xs text-neutral-500 font-mono" data-testid="ingest-disk">DB disk free {gb(job.disk_free)}</div>
      </div>
      <JobTable job={job} />
      <div className="flex items-center gap-3 text-xs text-neutral-600 flex-wrap">
        {pts != null && <span data-testid="ingest-points-sum">Points loaded: {n(pts)}</span>}
        {job.total_seconds && <span>Total {Math.round(job.total_seconds / 60)} min</span>}
        {job.post_refresh && <span>Refresh: {Object.entries(job.post_refresh).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>}
        <button className="k-btn k-btn-outline k-btn-sm" onClick={dl} data-testid="ingest-skipped-csv"><Download className="w-3.5 h-3.5" /> Skipped rows CSV</button>
        {active && <button className="k-btn k-btn-outline k-btn-sm" onClick={cancel} data-testid="ingest-cancel"><X className="w-3.5 h-3.5" /> Cancel</button>}
        {["interrupted", "needs_attention", "failed", "cancelled"].includes(job.status) && canStart && <button className="k-btn kazo-bg-burgundy k-btn-sm" onClick={resume} data-testid="ingest-resume"><Play className="w-3.5 h-3.5" /> Resume</button>}
      </div>
    </div>
  );
}

export default function IngestPanel({ file, onClose, canStart }) {
  const [jobs, setJobs] = useState(null);
  const load = () => api.get(`/data-files/${file.id}/ingests`).then((r) => setJobs(r.data.items));
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const running = jobs?.some((j) => ACTIVE.includes(j.status));
  const start = async () => {
    if (!window.confirm("Load this export into the loyalty database? Balances, tiers and dates are loaded exactly as in the file; no loyalty rules run.")) return;
    try { await api.post(`/data-files/${file.id}/ingest`); toast.success("Ingestion started"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };
  return (
    <div className="bg-white border border-black/10 p-6 space-y-4" data-testid="ingest-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] kazo-text-burgundy">Ingest · {file.filename}</div>
          <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Order: stores → customers → points/tiers → bills → SKU lines. Re-running adds no duplicates.</p>
        </div>
        <div className="flex gap-2">
          {canStart && <button className="k-btn kazo-bg-burgundy k-btn-sm" disabled={running} onClick={start} data-testid="ingest-start"><Play className="w-3.5 h-3.5" /> Start ingestion</button>}
          <button className="k-btn k-btn-outline k-btn-sm" onClick={onClose} data-testid="ingest-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {jobs?.length === 0 && <div className="text-sm text-neutral-500" data-testid="ingest-none">Not ingested yet.</div>}
      {jobs?.map((j) => <JobCard key={j.id} job={j} reload={load} canStart={canStart} />)}
    </div>
  );
}
